import { ReactElement, Children, cloneElement } from 'react';

import { IParentProps } from '../../shared/types/IParentProps';
import { IStyleableProps } from '../../shared/types/IStyleableProps';

import styles from './Button.module.css';
import { BUTTON_STYLE } from './BUTTON_STYLE';

const skin: Record<BUTTON_STYLE, string> = {
    [BUTTON_STYLE.PRIMARY]: [
        'bg-yellow-500',
        'rounded-full',
        'text-gray-900',
        'font-bold',
        'font-gellix',
        'inline-flex',
        'items-center',
        'justify-center',
        'hover:bg-yellow-800',
        'hover:text-gray-900',
        'disabled:bg-gray-100',
        'disabled:text-gray-600',
        'disabled:cursor-not-allowed',
        'focus:ring-4',
        'focus:ring-blue-500',
        'focus:bg-yellow-500',
        'focus:ring-inset',
        'outline-none',
        'active:bg-yellow-200',
        'active:ring-0',
    ].join(' '),
    [BUTTON_STYLE.SECONDARY]: [
        'border-2',
        'border-navy-500',
        'rounded-full',
        'text-gray-900',
        'font-bold',
        'font-gellix',
        'inline-flex',
        'items-center',
        'justify-center',
        'hover:border-yellow-500',
        'hover:text-gray-900',
        'disabled:border-gray-300',
        'disabled:text-gray-600',
        'disabled:cursor-not-allowed',
        'focus:border-blue-500',
        'outline-none',
        'active:border-yellow-200',
    ].join(' '),
    [BUTTON_STYLE.SECONDARY_LIGHT]: [
        'border-2',
        'border-white-500',
        'rounded-full',
        'inline-flex',
        'items-center',
        'justify-center',
        'disabled:opacity-75',
        'disabled:cursor-not-allowed',
        'text-white-500',
        'font-bold font-gellix',
        'hover:border-yellow-500',
        'hover:text-gray-900',
        'focus:border-blue-500',
        'outline-one',
        'active:border-yellow-200',
    ].join(' '),
    [BUTTON_STYLE.LINK]: [
        'text-gray-900',
        'font-bold',
        'font-gellix',
        'inline-flex',
        'flex-col',
        'cursor-pointer',
        'disabled:after:bg-gray-300',
        'disabled:text-gray-600',
        'disabled:cursor-not-allowed',
        'focus:ring',
        'focus:ring-blue-500',
        'outline-none',
        'active:after:bg-yellow-200',
        'active:ring-0',
        'after:w-full',
        'after:bg-yellow-500',
        'after:h-1/2',
        'after:mt-0.9',
        styles.link,
    ].join(' '),
    [BUTTON_STYLE.LINK_LIGHT]: [
        'text-white-500',
        'font-bold',
        'font-gellix',
        'inline-flex',
        'flex-col',
        'cursor-pointer',
        'disabled:after:bg-white-500',
        'disabled:text-white-500',
        'disabled:opacity-75',
        'disabled:cursor-not-allowed',
        'focus:ring',
        'focus:ring-blue-500',
        'outline-none',
        'active:after:bg-yellow-200',
        'active:ring-0',
        'after:w-full',
        'after:bg-yellow-500',
        'after:h-1/2',
        'after:mt-1',
        styles.link,
    ].join(' '),
};

type IStyledButtonProps = IParentProps<ReactElement> & IStyleableProps & {
    style?: BUTTON_STYLE;
};

export function StyledButton (props: IStyledButtonProps): ReactElement {
    const {
        style = BUTTON_STYLE.PRIMARY,
        children,
    }: IStyledButtonProps = props;

    // Ensure only a single child
    const child: ReactElement = Children.only(children);

    return cloneElement(child, {
        className: skin[style],
    });
}
